/**
 * Daily Brief — la primera pantalla del día.
 *
 * Piloto del contrato de tema V3: tipografía (display serif vs HUD),
 * geometría, textura, ornamento e ilustración salen del tema activo.
 * Regla de tipos: display para magnitudes y títulos, prosa para texto
 * que se lee en frases, mono para dato vivo (IDs, horas, rutas, estados).
 */

import { useQuery } from "@tanstack/react-query";
import { CorpoCard } from "../components/ui/CorpoCard";
import { CRTHeader } from "../components/ui/CRTHeader";
import { HexBadge } from "../components/ui/HexBadge";
import { StatTile } from "../components/ui/StatTile";
import { EmptyState } from "../components/ui/EmptyState";
import { SectionRule } from "../components/ui/Ornament";
import {
  MessageSquare, Dumbbell, DollarSign, Bell,
  Server, CheckSquare,
} from "lucide-react";

interface Session { id: string; title: string | null; turn_count: number; total_cost_usd: number }
interface Habit { id: number; name: string; streak: number; color: string; heatmap: { date: string; value: number }[] }
interface Service { name: string; state: string }
interface DailyCost { date: string; total_cost: number }

function greeting(h: number): string {
  if (h < 6) return "Madrugada larga";
  if (h < 12) return "Buenos días";
  if (h < 19) return "Buenas tardes";
  return "Buenas noches";
}

export function BriefRoute() {
  const { data: sessions } = useQuery({
    queryKey: ["brief", "sessions"],
    queryFn: () => fetch("/api/chat/sessions").then((r) => r.json()),
  });

  const { data: costs } = useQuery({
    queryKey: ["brief", "costs"],
    queryFn: () => fetch("/api/costs/summary").then((r) => r.json()),
  });

  // Serie corta solo para el sparkline del tile de gasto.
  const { data: daily } = useQuery({
    queryKey: ["brief", "costs", "daily"],
    queryFn: () => fetch("/api/costs/daily?days=14").then((r) => r.json()),
  });

  const { data: ops } = useQuery({
    queryKey: ["brief", "ops"],
    queryFn: () => fetch("/api/ops/status").then((r) => r.json()),
  });

  const { data: notifs } = useQuery({
    queryKey: ["brief", "notifs"],
    queryFn: () => fetch("/api/notifications?limit=5").then((r) => r.json()),
  });

  const { data: gymInsights } = useQuery({
    queryKey: ["brief", "gym"],
    queryFn: () => fetch("/api/gym/stats/insights").then((r) => r.json()),
  });

  const { data: habitsData } = useQuery({
    queryKey: ["brief", "habits"],
    queryFn: () => fetch("/api/habits/streaks").then((r) => r.json()),
  });

  const recentSessions: Session[] = (sessions?.sessions ?? []).slice(0, 5);
  const habits: Habit[] = habitsData?.habits ?? [];
  const services: Service[] = ops?.services ?? [];
  const unread: number = notifs?.unreadCount ?? 0;
  const costSpark = (daily?.daily ?? []).map((d: DailyCost) => d.total_cost);
  const running = services.filter((s) => s.state === "running").length;
  const allUp = services.length > 0 && running === services.length;

  const now = new Date();
  const today = now.toLocaleDateString("es-MX", { weekday: "long", day: "numeric", month: "long" });
  const clock = now.toLocaleTimeString("es-MX", { hour: "2-digit", minute: "2-digit" });
  const cost7d: number = costs?.cost_7d ?? 0;
  const costToday: number = costs?.cost_today ?? 0;
  const avg7d = cost7d / 7;
  const deltaPct = avg7d > 0 ? Math.round(((costToday - avg7d) / avg7d) * 100) : 0;

  return (
    <div className="p-4 md:p-8 max-w-[1400px] mx-auto">

      {/* ── Encabezado ───────────────────────────────────────────────── */}
      <header className="mb-2">
        <p className="bw-eyebrow text-[var(--bw-accent)] mb-2">
          {greeting(now.getHours())}
        </p>
        <CRTHeader text="Daily Brief" level={1} />
        <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1 mt-3">
          <p className="bw-prose text-[15px] text-[var(--bw-fg)] opacity-80 first-letter:uppercase">
            {today}
          </p>
          <span className="bw-code text-[11px] text-[var(--bw-muted)] tracking-[0.1em]">
            {clock}
          </span>
          <span
            className="bw-code text-[11px] tracking-[0.1em]"
            style={{ color: allUp ? "var(--bw-success)" : "var(--bw-warn)" }}
          >
            ● {allUp ? "TODOS LOS SERVICIOS EN PIE" : `${running}/${services.length} SERVICIOS`}
          </span>
        </div>
      </header>

      <SectionRule className="my-6" />

      {/* ── Métricas ─────────────────────────────────────────────────── */}
      <div className="grid gap-4 grid-cols-2 lg:grid-cols-4 mb-8">
        <StatTile
          label="Gasto hoy"
          icon={<DollarSign size={15} />}
          tone="warn"
          value={`$${costToday.toFixed(2)}`}
          unit="usd"
          foot={avg7d > 0 ? `${deltaPct >= 0 ? "▲" : "▼"} ${Math.abs(deltaPct)}% vs. media 7d` : undefined}
          footTone={deltaPct > 25 ? "danger" : deltaPct >= 0 ? "muted" : "success"}
          spark={costSpark}
        />
        <StatTile
          label="Notificaciones"
          icon={<Bell size={15} />}
          tone={unread > 0 ? "danger" : "muted"}
          value={unread}
          foot={unread > 0 ? "sin leer" : "bandeja limpia"}
          footTone={unread > 0 ? "danger" : "success"}
        />
        <StatTile
          label="Gym streak"
          icon={<Dumbbell size={15} />}
          tone="success"
          value={gymInsights?.consistency?.streak ?? 0}
          unit="días"
        />
        <StatTile
          label="Servicios"
          icon={<Server size={15} />}
          tone={allUp ? "accent" : "danger"}
          value={running}
          unit={`/ ${services.length}`}
          foot={allUp ? "sin incidencias" : "requiere atención"}
          footTone={allUp ? "success" : "danger"}
        />
      </div>

      {/* ── Sesiones y hábitos ───────────────────────────────────────── */}
      <div className="grid gap-5 lg:grid-cols-2 mb-6">
        <CorpoCard eyebrow="Chat" title="Sesiones recientes" elevation={2}>
          {recentSessions.length > 0 ? (
            <div className="divide-y divide-[var(--bw-border)]">
              {recentSessions.map((s) => (
                <div key={s.id} className="flex items-center gap-3 py-2.5 first:pt-0.5">
                  <MessageSquare size={14} className="text-[var(--bw-accent)] flex-shrink-0" />
                  <span className="bw-prose text-[14px] text-[var(--bw-fg)] truncate flex-1">
                    {s.title ?? <span className="bw-code text-[12px] text-[var(--bw-muted)]">{s.id.slice(0, 12)}…</span>}
                  </span>
                  <span className="bw-code text-[10px] text-[var(--bw-muted)] flex-shrink-0">
                    ${s.total_cost_usd?.toFixed(2) ?? "0.00"}
                  </span>
                  <HexBadge variant="muted" size="sm">{s.turn_count} turnos</HexBadge>
                </div>
              ))}
            </div>
          ) : (
            <EmptyState slot="no-sessions" compact />
          )}
        </CorpoCard>

        <CorpoCard eyebrow="Rutina" title="Hábitos" elevation={2}>
          {habits.length > 0 ? (
            <div className="divide-y divide-[var(--bw-border)]">
              {habits.map((h) => {
                const todayEntry = h.heatmap[h.heatmap.length - 1];
                const done = !!todayEntry && todayEntry.value > 0;
                return (
                  <div key={h.id} className="flex items-center gap-3 py-2.5 first:pt-0.5">
                    <CheckSquare
                      size={14}
                      className="flex-shrink-0"
                      style={{ color: done ? h.color : "var(--bw-muted)" }}
                    />
                    <span className="bw-prose text-[14px] text-[var(--bw-fg)] flex-1">{h.name}</span>
                    {h.streak > 0 && <HexBadge variant="primary" size="sm">{h.streak}d</HexBadge>}
                    <span
                      className="bw-code text-[10px] tracking-[0.1em]"
                      style={{ color: done ? "var(--bw-success)" : "var(--bw-muted)" }}
                    >
                      {done ? "HECHO" : "PENDIENTE"}
                    </span>
                  </div>
                );
              })}
            </div>
          ) : (
            <EmptyState
              slot="no-schedule"
              hint="No hay hábitos configurados. Créalos desde /habits para verlos aquí cada mañana."
              compact
            />
          )}
        </CorpoCard>
      </div>

      {/* ── Servicios ────────────────────────────────────────────────── */}
      {services.length > 0 && (
        <CorpoCard
          eyebrow="Infraestructura"
          title="Servicios"
          subtitle={allUp ? "Todo en pie." : "Hay servicios fuera de línea."}
          elevation={2}
          action={
            <HexBadge variant={allUp ? "success" : "danger"} size="sm">
              {running}/{services.length}
            </HexBadge>
          }
        >
          <div className="grid gap-x-6 gap-y-1 md:grid-cols-2">
            {services.map((svc) => {
              const up = svc.state === "running";
              return (
                <div key={svc.name} className="flex items-center gap-2.5 py-1.5">
                  <span
                    className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                    style={{
                      backgroundColor: up ? "var(--bw-success)" : "var(--bw-danger)",
                      boxShadow: up ? "0 0 6px var(--bw-success)" : "0 0 6px var(--bw-danger)",
                    }}
                  />
                  <span className="bw-code text-[12px] text-[var(--bw-fg)] truncate flex-1">{svc.name}</span>
                  <span
                    className="bw-code text-[10px] tracking-[0.12em] uppercase"
                    style={{ color: up ? "var(--bw-muted)" : "var(--bw-danger)" }}
                  >
                    {svc.state}
                  </span>
                </div>
              );
            })}
          </div>
        </CorpoCard>
      )}
    </div>
  );
}
